import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { catalogApi } from '@/lib/api';
import { ProductCard, ProductCardSkeleton } from '@/components/ProductCard';
import { Facets } from '@/components/Facets';
import { Seo } from '@/components/Seo';
import { useLocale } from '@/hooks/useLocalePath';
import type { JSX } from 'react';

/**
 * Thin categories get a real design, not an accidental one. `Podnóżki` has 9 products and
 * `Łóżka i materace` has 760 — the same six-wide grid cannot serve both.
 */
const THIN_THRESHOLD = 16;

/** The page parameter is user-visible and Polish, like the rest of the routing. */
const PAGE_KEY = 'strona';
const SIZE_KEY = 'na-stronie';
const DEFAULT_PAGE_SIZE = 24;
const PAGE_SIZES = [12, 24, 48, 96] as const;

/**
 * Pages to render: always the first and last, always the current and its neighbours, an
 * ellipsis for the rest. A 30-page category rendered 10 buttons and simply stopped, so the
 * last pages were unreachable except by editing the url.
 */
function pageWindow(current: number, total: number): Array<number | null> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const shown = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  return shown.flatMap((p, i) => (i > 0 && p - shown[i - 1]! > 1 ? [null, p] : [p]));
}

/** Previous and next, drawn as chevrons so the row reads left-to-right at a glance. */
function PageStep({
  direction,
  disabled,
  onClick,
  label,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="group flex h-9 w-9 items-center justify-center text-[var(--color-ink)] transition-[background-color,color,opacity] duration-[var(--dur-tap)] ease-[var(--ease-spring)] hover:bg-[var(--color-brand)] hover:text-[var(--color-ground-lift)] disabled:pointer-events-none disabled:opacity-30"
    >
      <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
        <path
          d={direction === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-transform duration-[var(--dur-hover)] ease-[var(--ease-spring)] motion-reduce:transition-none"
        />
      </svg>
    </button>
  );
}

export function CategoryPage(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale();
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter state lives in the URL, so a filtered view is linkable and the back button works.
  const requested = Number(params.get(SIZE_KEY) ?? DEFAULT_PAGE_SIZE);
  const pageSize = (PAGE_SIZES as readonly number[]).includes(requested) ? requested : DEFAULT_PAGE_SIZE;

  const query = {
    locale,
    category: slug,
    page: Number(params.get(PAGE_KEY) ?? 1),
    pageSize,
    sort: params.get('sort') ?? 'relevance',
    colors: params.get('kolor') ?? undefined,
    fabricFamilies: params.get('tkanina') ?? undefined,
    sizes: params.get('rozmiar') ?? undefined,
    availability: params.get('dostepnosc') ?? undefined,
    minPrice: params.get('cena-od') ?? undefined,
    maxPrice: params.get('cena-do') ?? undefined,
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['catalog', query],
    queryFn: () => catalogApi.list(query),
  });

  const { data: categories } = useQuery({
    queryKey: ['categories', locale],
    queryFn: () => catalogApi.categories(locale),
    staleTime: 5 * 60_000,
  });

  const category = (categories ?? [])
    .flatMap((c) => [c, ...c.children])
    .find((c) => c.slug === slug);

  const total = data?.total ?? 0;


  const FILTER_KEYS = ['kolor', 'tkanina', 'rozmiar', 'dostepnosc', 'cena-od', 'cena-do'];
  const activeFilterCount = FILTER_KEYS.reduce(
    (n, key) => n + (params.get(key) ?? '').split(',').filter(Boolean).length,
    0,
  );

  /**
   * A genuinely small category does not need a facet rail. A category that is *currently*
   * small because the customer just filtered it very much does.
   *
   * This read `total`, which is the filtered count, so applying a filter that left 16 or
   * fewer results unmounted the rail, the mobile filter button and the active-filter
   * badge together — removing the filters from the page along with every means of
   * undoing them. Clicking one colour could strand a customer with no way back except
   * the browser's back button.
   */
  const thin = !isLoading && activeFilterCount === 0 && total > 0 && total <= THIN_THRESHOLD;

  const setParam = (key: string, value: string | null): void => {
    // The facet rail sends this to reset everything; without it the "clear filters" link
    // deleted a parameter named `__clear` and appeared to do nothing.
    if (key === '__clear') {
      setParams(new URLSearchParams());
      return;
    }
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    // Changing a filter returns you to page one, because page 4 of the old result set is
    // meaningless against the new one. Changing the *page* obviously must not — this line
    // ran unconditionally, so every page button set `strona` and then deleted it on the
    // next statement, and pagination did nothing at all.
    if (key !== PAGE_KEY) next.delete(PAGE_KEY);
    setParams(next, { replace: false });
  };

  return (
    <>
      <Seo
        title={category?.name ?? t('nav.catalog')}
        description={t('catalog.resultsCount', { count: total })}
        path={`/kategoria/${slug}`}
        /* A filtered view must not spawn a duplicate indexable page. */
        noindex={params.toString().length > 0}
      />

      <div className="rz-shell py-10">
        <nav aria-label="breadcrumb" className="rz-eyebrow mb-4">
          {t('nav.catalog')} <span aria-hidden="true">/</span> {category?.name ?? slug}
        </nav>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-xl md:text-2xl">{category?.name ?? slug}</h1>
            <p className="mt-2 text-xs text-[var(--color-muted)]" data-testid="result-count">
              {t('catalog.resultsCount', { count: total })}
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <span className="text-[var(--color-muted)]">{t('catalog.sort')}</span>
            <select
              className="rz-field !w-auto !py-1.5 !text-xs"
              value={params.get('sort') ?? 'relevance'}
              onChange={(e) => setParam('sort', e.target.value)}
            >
              <option value="relevance">{t('catalog.sortRelevance')}</option>
              <option value="price_asc">{t('catalog.sortPriceAsc')}</option>
              <option value="price_desc">{t('catalog.sortPriceDesc')}</option>
              <option value="newest">{t('catalog.sortNewest')}</option>
              <option value="name">{t('catalog.sortName')}</option>
            </select>
          </label>
        </div>

        {thin && (
          <p className="mt-5 max-w-lg text-sm text-[var(--color-ink-soft)] leading-relaxed border-l-2 border-[var(--color-accent)] pl-4">
            {t('catalog.thinCategoryNote', { count: total })}
          </p>
        )}
      </div>

      {/* On a phone the facet rail sits above the grid, so a customer scrolls past sixty
          controls before seeing one product. Below `lg` it becomes a sheet instead. */}
      {!thin && (
        <div className="rz-shell mb-4 lg:hidden">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="rz-button rz-button--ghost w-full !justify-between"
            aria-haspopup="dialog"
            data-testid="open-filters"
          >
            <span>{t('catalog.filters')}</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-brand)] px-1.5 text-2xs font-bold text-[var(--color-ground)]">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      )}

      <div className="rz-shell grid min-w-0 gap-8 pb-24 lg:grid-cols-[240px_minmax(0,1fr)]">
        {/* A 9-product category does not need a facet rail. */}
        {!thin && (
          <div className="hidden lg:block">
            <Facets facets={data?.facets} params={params} onChange={setParam} />
          </div>
        )}

        <div className={`min-w-0 ${thin ? 'lg:col-span-2' : ''}`}>
          {isLoading ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }, (_, i) => <ProductCardSkeleton key={i} />)}
            </div>
          ) : isError ? (
            /* A failed request is not an empty result. Reporting it as "nothing matches
               your filters" sends the customer to fix a filter that was never the
               problem, and hides an outage behind a plausible-looking empty state. */
            <div className="py-20 text-center space-y-3" data-testid="catalog-error">
              <p className="font-display text-lg">{t('catalog.errorTitle')}</p>
              <p className="text-sm text-[var(--color-muted)]">{t('catalog.errorBody')}</p>
              <button type="button" onClick={() => void refetch()} className="rz-button rz-button--ghost mt-2">
                {t('catalog.retry')}
              </button>
            </div>
          ) : total === 0 ? (
            <div className="py-20 text-center space-y-3">
              <p className="font-display text-lg">{t('catalog.emptyTitle')}</p>
              <p className="text-sm text-[var(--color-muted)]">{t('catalog.emptyBody')}</p>
              <button type="button" onClick={() => setParams(new URLSearchParams())} className="rz-button rz-button--ghost mt-2">
                {t('catalog.clearFilters')}
              </button>
            </div>
          ) : (
            <>
              <div
                className={
                  thin
                    ? 'grid min-w-0 grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3'
                    : 'grid min-w-0 grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 xl:grid-cols-4'
                }
                data-testid="product-grid"
              >
                {(data?.items ?? []).map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>

              {/* Page size and pagination share a row: both answer "how much of this am
                  I looking at", and separating them makes the second one look like the
                  only control. */}
              {((data?.totalPages ?? 1) > 1 || (data?.total ?? 0) > PAGE_SIZES[0]) && (
                <div className="mt-14 flex flex-col items-center gap-6 sm:flex-row sm:justify-between">
                  <label className="flex items-center gap-2 text-xs text-[var(--color-ink-soft)]">
                    {t('catalog.perPage')}
                    <span className="inline-flex overflow-hidden ring-1 ring-[var(--color-line)]">
                      {PAGE_SIZES.map((size) => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => setParam(SIZE_KEY, size === DEFAULT_PAGE_SIZE ? null : String(size))}
                          aria-pressed={size === pageSize}
                          className={`min-w-9 px-3 py-1.5 text-xs font-semibold transition-[background-color,color] duration-[var(--dur-tap)] ease-[var(--ease-spring)] ${
                            size === pageSize
                              ? 'bg-[var(--color-ink)] text-[var(--color-ground-lift)]'
                              : 'text-[var(--color-ink)] hover:bg-[var(--color-brand)]/10'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </span>
                  </label>

                  {(data?.totalPages ?? 1) > 1 && (
                    <nav className="flex items-center gap-1" aria-label="pagination">
                      <PageStep
                        direction="prev"
                        disabled={(data?.page ?? 1) <= 1}
                        onClick={() => setParam(PAGE_KEY, String((data?.page ?? 1) - 1))}
                        label={t('catalog.previousPage')}
                      />
                      {pageWindow(data?.page ?? 1, data?.totalPages ?? 1).map((page, i) =>
                        page === null ? (
                          <span key={`gap-${i}`} className="px-1 text-xs text-[var(--color-muted)]">
                            &hellip;
                          </span>
                        ) : (
                          <button
                            key={page}
                            type="button"
                            onClick={() => setParam(PAGE_KEY, String(page))}
                            aria-current={page === (data?.page ?? 1) ? 'page' : undefined}
                            className={`relative h-9 min-w-9 px-2 text-xs font-semibold transition-[background-color,color,transform] duration-[var(--dur-tap)] ease-[var(--ease-spring)] active:scale-95 motion-reduce:active:scale-100 ${
                              page === (data?.page ?? 1)
                                ? 'bg-[var(--color-ink)] text-[var(--color-ground-lift)]'
                                : 'text-[var(--color-ink)] hover:bg-[var(--color-brand)] hover:text-[var(--color-ground-lift)]'
                            }`}
                          >
                            {page}
                          </button>
                        ),
                      )}
                      <PageStep
                        direction="next"
                        disabled={(data?.page ?? 1) >= (data?.totalPages ?? 1)}
                        onClick={() => setParam(PAGE_KEY, String((data?.page ?? 1) + 1))}
                        label={t('catalog.nextPage')}
                      />
                    </nav>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {filtersOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[55] bg-[var(--color-ink)]/40 lg:hidden"
              onClick={() => setFiltersOpen(false)}
              aria-hidden="true"
            />
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 34 }}
              role="dialog"
              aria-label={t('catalog.filters')}
              data-testid="filter-sheet"
              className="fixed inset-x-0 bottom-0 z-[60] flex max-h-[85vh] flex-col bg-[var(--color-ground-lift)] lg:hidden"
            >
              <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-4">
                <h2 className="font-display text-lg">{t('catalog.filters')}</h2>
                <button
                  type="button"
                  onClick={() => setFiltersOpen(false)}
                  className="-mr-2 inline-flex min-h-11 shrink-0 items-center px-2 text-xs font-semibold"
                >
                  {t('common.close')}
                </button>
              </div>
              <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-5 pb-4">
                <Facets facets={data?.facets} params={params} onChange={setParam} showHeader={false} />
              </div>
              <div className="flex gap-2 px-5 pb-5">
                {activeFilterCount > 0 && (
                  <button type="button" className="rz-button rz-button--ghost" onClick={() => setParams(new URLSearchParams())}>
                    {t('catalog.clearFilters')}
                  </button>
                )}
                <button type="button" className="rz-button flex-1" onClick={() => setFiltersOpen(false)}>
                  {t('catalog.applyFilters')} ({total})
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
